/**
 * ============================================================================
 * LENA - EUROFARMA COLOR SYSTEM
 * ============================================================================
 *
 * Apple-inspired design with Eurofarma Lena brand identity
 *
 * OFFICIAL BRAND COLORS:
 * - PANTONE 333 C (Green) - #00916B
 * - PANTONE 287 C (Blue)  - #0033A0
 *
 * HOW TO CHANGE COLORS:
 * 1. Edit primary colors in BRAND_COLORS below
 * 2. Other colors are derived automatically
 * 3. To completely change theme, edit only BRAND_COLORS
 *
 * ============================================================================
 */

// ============================================================================
// BRAND COLORS (EDIT HERE TO CHANGE THE ENTIRE THEME)
// ============================================================================
export const BRAND_COLORS = {
    // Official Eurofarma Lena colors (Pantone)
    pantone333: '#00916B',  // PANTONE 333 C - Main green
    pantone287: '#0033A0',  // PANTONE 287 C - Main blue

    // Lena green variations (derived from Pantone 333)
    greenDarker: '#006B4F',   // Darker green
    greenDark: '#007A5B',     // Dark green
    greenMain: '#00916B',     // Main green (Pantone 333)
    greenMedium: '#00A67E',   // Medium green
    greenLight: '#00B67A',    // Light green
    greenLighter: '#33C794',  // Lighter green
    greenPale: '#E6F7F2',     // Pale green (backgrounds)

    // Lena blue variations (derived from Pantone 287)
    blueDarker: '#002470',    // Darker blue
    blueDark: '#002B85',      // Dark blue
    blueMain: '#0033A0',      // Main blue (Pantone 287)
    blueMedium: '#0047D1',    // Medium blue
    blueLight: '#005AE6',     // Light blue
    blueLighter: '#3380FF',   // Lighter blue
    bluePale: '#E6EFFF',      // Pale blue (backgrounds)
};

// ============================================================================
// NEUTRAL PALETTE (APPLE STYLE)
// ============================================================================
export const NEUTRAL_COLORS = {
    white: '#FFFFFF',
    gray50: '#FAFAFA',         // Lightest background
    gray100: '#F5F5F7',        // Apple's favorite gray
    gray200: '#E8E8ED',        // Subtle borders
    gray300: '#D2D2D7',        // Dividers
    gray400: '#B0B0B8',        // Disabled text
    gray500: '#8E8E93',        // Secondary text
    gray600: '#6C6C70',        // Body text
    gray700: '#48484A',        // Dark text
    gray800: '#2C2C2E',        // Almost black
    gray900: '#1C1C1E',        // Pure black (dark mode)
    black: '#000000',
};

// ============================================================================
// SYSTEM COLORS (APPLE STYLE)
// ============================================================================
export const SYSTEM_COLORS = {
    // Status
    success: BRAND_COLORS.greenMain,
    error: '#FF3B30',          // Apple red
    warning: '#FF9500',        // Apple orange
    info: BRAND_COLORS.blueMain,

    // Additional system colors
    red: '#FF3B30',
    orange: '#FF9500',
    yellow: '#FFCC00',
    green: BRAND_COLORS.greenMain,
    teal: '#5AC8FA',
    blue: BRAND_COLORS.blueMain,
    indigo: '#5856D6',
    purple: '#AF52DE',
    pink: '#FF2D55',
};

// ============================================================================
// THEME CONFIGURATION
// ============================================================================

/**
 * AVAILABLE THEME OPTIONS:
 *
 * 'green-header'  - Lena green header with blue accents (Recommended)
 * 'blue-header'   - Blue header with green accents
 * 'white-header'  - Minimalist white header
 * 'gradient'      - Green-blue gradient header
 */
export const ACTIVE_THEME = 'blue-header'; // CHANGE HERE TO SWITCH THEME

// ============================================================================
// AVAILABLE THEMES
// ============================================================================
const THEME_VARIANTS = {
    /**
     * GREEN THEME (RECOMMENDED)
     * Lena green header with blue accent
     */
    'green-header': {
        name: 'Lena Green Premium',
        description: 'Eurofarma green header with sophisticated blue accents',

        primary: {
            main: BRAND_COLORS.blueMain,      // Blue for accents/buttons
            light: BRAND_COLORS.blueLighter,
            dark: BRAND_COLORS.blueDark,
            darker: BRAND_COLORS.blueDarker,
            contrastText: NEUTRAL_COLORS.white,
        },

        secondary: {
            main: BRAND_COLORS.greenMain,     // Green for secondary elements
            light: BRAND_COLORS.greenLight,
            dark: BRAND_COLORS.greenDark,
            contrastText: NEUTRAL_COLORS.white,
        },

        header: {
            background: BRAND_COLORS.greenMain,
            text: NEUTRAL_COLORS.white,
            border: BRAND_COLORS.greenDark,
            accent: BRAND_COLORS.blueLight,
            hover: 'rgba(255, 255, 255, 0.1)',
        },

        sidebar: {
            background: NEUTRAL_COLORS.white,
            text: NEUTRAL_COLORS.gray900,
            textSecondary: NEUTRAL_COLORS.gray500,
            hover: 'rgba(0, 0, 0, 0.04)',
            active: BRAND_COLORS.blueMain,
            activeBackground: BRAND_COLORS.bluePale,
            border: NEUTRAL_COLORS.gray200,
        },

        login: {
            main: BRAND_COLORS.greenMain,
            light: BRAND_COLORS.greenLight,
            dark: BRAND_COLORS.greenDark,
            gradient: `linear-gradient(135deg, ${BRAND_COLORS.greenDark} 0%, ${BRAND_COLORS.greenMain} 50%, ${BRAND_COLORS.blueMain} 100%)`,
        },
    },

    /**
     * TEMA AZUL
     * Header azul com detalhes verdes
     */
    'blue-header': {
        name: 'Lena Azul Premium',
        description: 'Header azul Eurofarma com detalhes verdes sofisticados',

        primary: {
            main: BRAND_COLORS.blueMain,
            light: BRAND_COLORS.blueLight,
            dark: BRAND_COLORS.blueDark,
            darker: BRAND_COLORS.blueDarker,
            contrastText: NEUTRAL_COLORS.white,
        },

        secondary: {
            main: BRAND_COLORS.blueMain,
            light: BRAND_COLORS.blueLight,
            dark: BRAND_COLORS.blueDark,
            contrastText: NEUTRAL_COLORS.white,
        },

        header: {
            background: BRAND_COLORS.blueMain,
            text: NEUTRAL_COLORS.white,
            border: BRAND_COLORS.blueDark,
            accent: BRAND_COLORS.greenLight,
            hover: 'rgba(255, 255, 255, 0.1)',
            button: NEUTRAL_COLORS.black,
        },

        sidebar: {
            background: NEUTRAL_COLORS.white,
            text: NEUTRAL_COLORS.gray900,
            textSecondary: NEUTRAL_COLORS.gray500,
            hover: 'rgba(0, 0, 0, 0.04)',
            active: BRAND_COLORS.greenMain,
            activeBackground: BRAND_COLORS.greenPale,
            border: NEUTRAL_COLORS.gray200,
        },

        login: {
            main: BRAND_COLORS.blueMain,
            light: BRAND_COLORS.blueLight,
            dark: BRAND_COLORS.blueDark,
            gradient: `linear-gradient(135deg, ${BRAND_COLORS.blueDark} 0%, ${BRAND_COLORS.blueMain} 50%, ${BRAND_COLORS.greenMain} 100%)`,
        },
    },

    /**
     * TEMA MINIMALISTA
     * Header branco com detalhes coloridos
     */
    'white-header': {
        name: 'Lena Minimalista',
        description: 'Header branco clean com detalhes coloridos',

        primary: {
            main: BRAND_COLORS.blueMain,
            light: BRAND_COLORS.blueLight,
            dark: BRAND_COLORS.blueDark,
            darker: BRAND_COLORS.blueDarker,
            contrastText: NEUTRAL_COLORS.white,
        },

        secondary: {
            main: BRAND_COLORS.greenMain,
            light: BRAND_COLORS.greenLight,
            dark: BRAND_COLORS.greenDark,
            contrastText: NEUTRAL_COLORS.white,
        },

        header: {
            background: NEUTRAL_COLORS.white,
            text: NEUTRAL_COLORS.gray900,
            border: NEUTRAL_COLORS.gray200,
            accent: BRAND_COLORS.blueMain,
            hover: NEUTRAL_COLORS.gray100,
        },

        sidebar: {
            background: NEUTRAL_COLORS.gray100,
            text: NEUTRAL_COLORS.gray900,
            textSecondary: NEUTRAL_COLORS.gray500,
            hover: 'rgba(0, 0, 0, 0.04)',
            active: BRAND_COLORS.blueMain,
            activeBackground: BRAND_COLORS.bluePale,
            border: NEUTRAL_COLORS.gray200,
        },

        login: {
            main: BRAND_COLORS.greenMain,
            light: BRAND_COLORS.greenLight,
            dark: BRAND_COLORS.greenDark,
            gradient: `linear-gradient(135deg, ${BRAND_COLORS.greenMain} 0%, ${BRAND_COLORS.blueMain} 100%)`,
        },
    },

    /**
     * TEMA GRADIENTE
     * Header com gradiente verde-azul
     */
    'gradient': {
        name: 'Lena Gradiente',
        description: 'Header gradiente verde-azul moderno',

        primary: {
            main: BRAND_COLORS.blueMain,
            light: BRAND_COLORS.blueLight,
            dark: BRAND_COLORS.blueDark,
            darker: BRAND_COLORS.blueDarker,
            contrastText: NEUTRAL_COLORS.white,
        },

        secondary: {
            main: BRAND_COLORS.greenMain,
            light: BRAND_COLORS.greenLight,
            dark: BRAND_COLORS.greenDark,
            contrastText: NEUTRAL_COLORS.white,
        },

        header: {
            background: `linear-gradient(90deg, ${BRAND_COLORS.greenMain} 0%, ${BRAND_COLORS.blueMain} 100%)`,
            text: NEUTRAL_COLORS.white,
            border: 'transparent',
            accent: NEUTRAL_COLORS.white,
            hover: 'rgba(255, 255, 255, 0.1)',
        },

        sidebar: {
            background: NEUTRAL_COLORS.white,
            text: NEUTRAL_COLORS.gray900,
            textSecondary: NEUTRAL_COLORS.gray500,
            hover: 'rgba(0, 0, 0, 0.04)',
            active: BRAND_COLORS.greenMain,
            activeBackground: BRAND_COLORS.greenPale,
            border: NEUTRAL_COLORS.gray200,
        },

        login: {
            main: BRAND_COLORS.greenMain,
            light: BRAND_COLORS.greenLight,
            dark: BRAND_COLORS.greenDark,
            gradient: `linear-gradient(135deg, ${BRAND_COLORS.greenMain} 0%, ${BRAND_COLORS.blueMain} 100%)`,
        },
    },
};

// ============================================================================
// TEMA ATIVO (EXPORTADO)
// ============================================================================
export const THEME = THEME_VARIANTS[ACTIVE_THEME] || THEME_VARIANTS['green-header'];

// ============================================================================
// GRADIENTES
// ============================================================================
export const GRADIENTS = {
    primary: `linear-gradient(135deg, ${BRAND_COLORS.blueMain} 0%, ${BRAND_COLORS.blueLight} 100%)`,
    secondary: `linear-gradient(135deg, ${BRAND_COLORS.greenDark} 0%, ${BRAND_COLORS.greenMain} 100%)`,
    greenBlue: `linear-gradient(135deg, ${BRAND_COLORS.greenMain} 0%, ${BRAND_COLORS.blueMain} 100%)`,
    soft: `linear-gradient(180deg, ${NEUTRAL_COLORS.white} 0%, ${NEUTRAL_COLORS.gray100} 100%)`,
    overlay: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.05) 100%)',
    glass: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)',
};

// ============================================================================
// SOMBRAS (ESTILO APPLE)
// ============================================================================
export const SHADOWS = {
    xs: '0 1px 2px rgba(0, 0, 0, 0.06)',
    sm: '0 2px 8px rgba(0, 0, 0, 0.08)',
    md: '0 4px 16px rgba(0, 0, 0, 0.10)',
    lg: '0 8px 24px rgba(0, 0, 0, 0.12)',
    xl: '0 16px 48px rgba(0, 0, 0, 0.14)',

    // Sombras coloridas (opcional)
    greenSm: `0 4px 12px ${BRAND_COLORS.greenMain}20`,
    greenMd: `0 8px 24px ${BRAND_COLORS.greenMain}30`,
    blueSm: `0 4px 12px ${BRAND_COLORS.blueMain}20`,
    blueMd: `0 8px 24px ${BRAND_COLORS.blueMain}30`,
};

// ============================================================================
// BACKGROUNDS
// ============================================================================
export const BACKGROUNDS = {
    default: NEUTRAL_COLORS.white,
    secondary: NEUTRAL_COLORS.gray100,
    elevated: NEUTRAL_COLORS.white,
    dark: NEUTRAL_COLORS.gray900,

    // Backgrounds com opacidade
    glassLight: 'rgba(255, 255, 255, 0.8)',
    glassDark: 'rgba(28, 28, 30, 0.8)',
};

// ============================================================================
// TEXTOS
// ============================================================================
export const TEXT_COLORS = {
    primary: NEUTRAL_COLORS.gray900,
    secondary: NEUTRAL_COLORS.gray600,
    tertiary: NEUTRAL_COLORS.gray500,
    disabled: NEUTRAL_COLORS.gray400,
    inverse: NEUTRAL_COLORS.white,
    link: BRAND_COLORS.blueMain,
    linkHover: BRAND_COLORS.blueDark,
};

// ============================================================================
// EXPORTAÇÃO CONSOLIDADA
// ============================================================================
export const LENA_COLORS = {
    brand: BRAND_COLORS,
    neutral: NEUTRAL_COLORS,
    system: SYSTEM_COLORS,
    theme: THEME,
    gradients: GRADIENTS,
    shadows: SHADOWS,
    backgrounds: BACKGROUNDS,
    text: TEXT_COLORS,
};

export default LENA_COLORS;
